export interface NameValidationResult {
  isValid: boolean;
  normalizedName: string;
  error?: string;
}

const MAX_NAME_LENGTH = 80;
const ALLOWED_NAME_CHARS = /^[\p{L}\s.',\-\u2019]+$/u;
const NAME_TOKEN_PATTERN = /^(?=.*\p{L})[\p{L}]+(?:[.'\-\u2019][\p{L}]+)*(?:,)?\.?$/u;
const MIN_MEANINGFUL_NAME_PARTS = 2;

export function normalizeHumanName(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

export function validateHumanName(value: string | null | undefined): NameValidationResult {
  const normalizedName = normalizeHumanName(value);

  if (!normalizedName) {
    return {
      isValid: false,
      normalizedName,
      error: 'Name is required.',
    };
  }

  if (normalizedName.length < 2) {
    return {
      isValid: false,
      normalizedName,
      error: 'Name must be at least 2 characters long.',
    };
  }

  if (normalizedName.length > MAX_NAME_LENGTH) {
    return {
      isValid: false,
      normalizedName,
      error: `Name must be ${MAX_NAME_LENGTH} characters or fewer.`,
    };
  }

  if (!ALLOWED_NAME_CHARS.test(normalizedName)) {
    return {
      isValid: false,
      normalizedName,
      error: 'Name can only contain letters, spaces, apostrophes, hyphens, commas, and periods.',
    };
  }

  const letters = normalizedName.match(/\p{L}/gu) ?? [];
  if (letters.length < 2) {
    return {
      isValid: false,
      normalizedName,
      error: 'Name must contain at least 2 letters.',
    };
  }

  const invalidToken = normalizedName.split(' ').find((token) => !NAME_TOKEN_PATTERN.test(token));
  if (invalidToken) {
    return {
      isValid: false,
      normalizedName,
      error: 'Enter a real name format, such as Juan Dela Cruz or Maria A. Santos.',
    };
  }

  const meaningfulNameParts = normalizedName
    .split(' ')
    .filter((token) => (token.match(/\p{L}/gu) ?? []).length >= 2);

  if (meaningfulNameParts.length < MIN_MEANINGFUL_NAME_PARTS) {
    return {
      isValid: false,
      normalizedName,
      error: 'Enter your full name with at least first name and last name.',
    };
  }

  return {
    isValid: true,
    normalizedName,
  };
}